import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@tempo-io/sui-accordion';
import { Button } from '@tempo-io/sui-button';
import { ArrowOutward } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DependencyType, type Item, type ItemDependency } from '@/lib/api/types/items';
import { useItemPanelNavigation } from '@/lib/hooks/item-panel/useItemPanelParams';
import { useItemsBulkFetchQuery } from '@/lib/queries/common/useGetItemsBulkFetchQuery';
import { useBulkFetchTimelineItems } from '@/lib/queries/loop-planner/useBulkFetchTimelineItems';
import { useGetSchedulesByItemIds } from '@/lib/queries/work-integrations/useGetSchedules';
import { TabValue } from '@/lib/types/itemPanel';
import { DateFormat, formatLocalDateWithFallback } from '@/lib/utils/common/dateFormats';
import { STALE_TIME_MS } from './TimelineItemConstants';
import type { TimelineScenarioContext } from './useTimelineTableColumns';

interface DependencyDates {
  name: string;
  expectedStart: string | null;
  expectedFinish: string | null;
}

const useDependencyDates = ({
  dependencyIds,
  scenarioContext,
  enabled,
}: {
  dependencyIds: number[];
  scenarioContext: TimelineScenarioContext | undefined;
  enabled: boolean;
}): Map<number, DependencyDates> => {
  const inScenario = scenarioContext !== undefined;

  const { data: timelineItems = [] } = useBulkFetchTimelineItems({
    loopId: scenarioContext?.loopId ?? 0,
    scenarioId: scenarioContext?.scenarioId ?? 0,
    itemIds: dependencyIds,
    enabled: enabled && inScenario,
    staleTime: STALE_TIME_MS,
  });

  const { data: dependencyItems = [] } = useItemsBulkFetchQuery(
    { itemIds: dependencyIds },
    STALE_TIME_MS,
    enabled && !inScenario,
  );
  const { data: schedules } = useGetSchedulesByItemIds(
    dependencyIds,
    STALE_TIME_MS,
    enabled && !inScenario,
  );

  return useMemo(() => {
    if (inScenario) {
      return new Map(
        timelineItems.map((timelineItem) => [
          timelineItem.scenarioItem.itemId,
          {
            name: timelineItem.scenarioItem.name,
            expectedStart: timelineItem.scenarioItemMetrics?.expectedStart ?? null,
            expectedFinish: timelineItem.scenarioItemMetrics?.expectedFinish ?? null,
          },
        ]),
      );
    }
    return new Map(
      dependencyItems.map((item: Item) => [
        item.id,
        {
          name: item.name,
          expectedStart: schedules?.[item.id]?.expectedStartDateTime ?? null,
          expectedFinish: schedules?.[item.id]?.expectedFinishDateTime ?? null,
        },
      ]),
    );
  }, [inScenario, timelineItems, dependencyItems, schedules]);
};

const ACCORDION_VALUE = 'dependencies';

const splitDependencyIds = (dependencies: ItemDependency[]) => {
  const predecessorIds: number[] = [];
  const successorIds: number[] = [];
  for (const dependency of dependencies) {
    if (dependency.dependencyType === DependencyType.Predecessor) {
      predecessorIds.push(dependency.dependencyItemId);
    } else if (dependency.dependencyType === DependencyType.Successor) {
      successorIds.push(dependency.dependencyItemId);
    }
  }
  return { predecessorIds, successorIds };
};

const DependencyRow = ({ name, dateLabel }: { name: string; dateLabel: string }) => {
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="min-w-0 cursor-default truncate text-left text-text-neutral-secondary"
          >
            {name}:
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-100">
          {name}
        </TooltipContent>
      </Tooltip>
      <span className="text-right text-text-neutral-secondary whitespace-nowrap">{dateLabel}</span>
    </>
  );
};

export const TimelineItemTooltipDependencies = ({
  itemId,
  dependencies,
  isTooltipOpen,
  scenarioContext,
}: {
  itemId: number;
  dependencies: ItemDependency[];
  isTooltipOpen: boolean;
  scenarioContext?: TimelineScenarioContext;
}) => {
  const { t } = useTranslation();
  const { openItemPanel } = useItemPanelNavigation();

  const { predecessorIds, successorIds } = useMemo(
    () => splitDependencyIds(dependencies),
    [dependencies],
  );
  const dependencyIds = useMemo(
    () => [...predecessorIds, ...successorIds],
    [predecessorIds, successorIds],
  );

  const enabled = isTooltipOpen && dependencyIds.length > 0;

  const datesById = useDependencyDates({ dependencyIds, scenarioContext, enabled });

  const sections = useMemo(() => {
    const formatDate = (dateString: string | null | undefined) =>
      formatLocalDateWithFallback({
        dateString,
        formatStr: DateFormat.NUMERIC_DATE,
        fallback: t(($) => $.common.notAvailable),
      });
    const toRows = (ids: number[], type: DependencyType) =>
      ids
        .map((id) => {
          const dates = datesById.get(id);
          return dates ? { id, ...dates } : undefined;
        })
        .filter((row): row is { id: number } & DependencyDates => row !== undefined)
        .map((row) => {
          const dateLabel =
            type === DependencyType.Successor
              ? t(($) => $.loopVisualization.timelineTooltip.expectedStart, {
                  date: formatDate(row.expectedStart),
                })
              : t(($) => $.loopVisualization.timelineTooltip.expectedFinish, {
                  date: formatDate(row.expectedFinish),
                });
          return {
            id: row.id,
            name: row.name,
            dateLabel,
          };
        });
    return [
      {
        key: DependencyType.Predecessor,
        label: t(($) => $.loopVisualization.timelineTooltip.predecessor),
        rows: toRows(predecessorIds, DependencyType.Predecessor),
      },
      {
        key: DependencyType.Successor,
        label: t(($) => $.loopVisualization.timelineTooltip.successor),
        rows: toRows(successorIds, DependencyType.Successor),
      },
    ].filter((section) => section.rows.length > 0);
  }, [datesById, predecessorIds, successorIds, t]);

  if (dependencyIds.length === 0) {
    return null;
  }

  return (
    <Accordion type="single" collapsible defaultValue={ACCORDION_VALUE}>
      <AccordionItem value={ACCORDION_VALUE} className="border-0">
        <div className="flex items-center gap-1">
          <span className="flex-1 font-bold">
            {t(($) => $.loopVisualization.timelineTooltip.dependencies)}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t(($) => $.loopVisualization.timelineTooltip.openDependencies)}
            onClick={() => openItemPanel({ itemId, panelTab: TabValue.DEPENDENCIES })}
          >
            <ArrowOutward />
          </Button>
          <AccordionTrigger
            className="w-auto shrink-0 items-center py-0 hover:no-underline [&>svg]:translate-y-0"
            aria-label={t(($) => $.loopVisualization.timelineTooltip.dependencies)}
          />
        </div>
        <AccordionContent>
          <div className="rounded-lg border-2 p-3 text-xs">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 items-center">
              {sections.map((section) => (
                <Fragment key={section.key}>
                  <span className="col-span-2 font-bold">{section.label}:</span>
                  {section.rows.map((row) => (
                    <DependencyRow key={row.id} name={row.name} dateLabel={row.dateLabel} />
                  ))}
                </Fragment>
              ))}
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
